import { apiGet, apiPost, apiDelete } from './api';
import type {
  Event,
  EventFilters,
  PaginatedEventsResponse,
  EventStats,
  PaginationParams,
  SortParams,
} from '../types';

export interface CreateEventInput {
  title: string;
  description?: string | undefined;
  type: string;
  location: string;
  venue?: string | undefined;
  startDate: string;
  endDate?: string | undefined;
  capacity?: number | undefined;
  website?: string | undefined;
  ticketUrl?: string | undefined;
  price?: string | undefined;
  isPublic?: boolean;
}

class EventService {
  /**
   * Create a new event
   */
  async createEvent(eventData: CreateEventInput | FormData): Promise<Event> {
    // Handle both FormData and regular objects
    if (eventData instanceof FormData) {
      // Let browser set multipart boundary for FormData
      return apiPost<Event>('/events', eventData);
    }
    return apiPost<Event>('/events', eventData);
  }

  /**
   * Get the current user's own events (all statuses), newest first.
   */
  async getMyEvents(pagination?: PaginationParams): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams();

    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    const queryString = params.toString();
    const url = queryString ? `/events/mine?${queryString}` : '/events/mine';

    return apiGet<PaginatedEventsResponse>(url);
  }

  /**
   * Get all events with optional filters and pagination
   */
  async getEvents(
    filters?: EventFilters,
    pagination?: PaginationParams,
    sort?: SortParams,
  ): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.upcoming !== undefined) params.append('upcoming', filters.upcoming.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.startDateFrom) params.append('startDateFrom', filters.startDateFrom);
    if (filters?.startDateTo) params.append('startDateTo', filters.startDateTo);
    if (filters?.type) params.append('type', filters.type);
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    
    if (sort?.sortBy) params.append('sortBy', sort.sortBy);
    if (sort?.sortOrder) params.append('sortOrder', sort.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `/events?${queryString}` : '/events';
    
    return apiGet<PaginatedEventsResponse>(url);
  }

  /**
   * Get a specific event by ID
   */
  async getEvent(eventId: number): Promise<Event> {
    return apiGet<Event>(`/events/${eventId}`);
  }

  /**
   * Get a specific event by slug
   */
  async getEventBySlug(slug: string): Promise<Event> {
    return apiGet<Event>(`/events/${slug}`);
  }

  /**
   * Get active events (currently happening or upcoming)
   */
  async getActiveEvents(options?: PaginationParams & { search?: string }): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams();
    params.append('isActive', 'true');

    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    return apiGet<PaginatedEventsResponse>(`/events?${params.toString()}`);
  }

  /**
   * Get upcoming events
   */
  async getUpcomingEvents(options?: { 
    search?: string;
    limit?: number;
    page?: number;
  }): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams();
    params.append('upcoming', 'true');
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    return apiGet<PaginatedEventsResponse>(`/events?${params.toString()}`);
  }

  /**
   * Search events
   */
  async searchEvents(
    query: string,
    filters?: Omit<EventFilters, 'search'>,
    pagination?: PaginationParams,
  ): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams();
    params.append('search', query);
    
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.startDateFrom) params.append('startDateFrom', filters.startDateFrom);
    if (filters?.startDateTo) params.append('startDateTo', filters.startDateTo);
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    return apiGet<PaginatedEventsResponse>(`/events/search?${params.toString()}`);
  }

  /**
   * Get event statistics for dashboard
   */
  async getEventStats(): Promise<EventStats> {
    return apiGet<EventStats>('/events/stats');
  }

  /**
   * Cancel an event (soft delete → status CANCELLED). Allowed for the
   * creator/organizer only while the event has no groups yet; afterwards the
   * server responds with 409 EVENT_HAS_GROUPS.
   */
  async deleteEvent(eventId: number): Promise<void> {
    return apiDelete(`/events/${eventId}`);
  }
}

class AdminEventService {
  /**
   * Get pending events for admin approval
   */
  async getPendingEvents(): Promise<Event[]> {
    return apiGet<Event[]>('/events/admin/pending');
  }

  /**
   * Approve an event
   */
  async approveEvent(eventId: number): Promise<Event> {
    return apiPost<Event>(`/events/${eventId}/approve`);
  }

  /**
   * Reject an event
   */
  async rejectEvent(eventId: number, reason?: string): Promise<Event> {
    return apiPost<Event>(`/events/${eventId}/reject`, { reason });
  }

  /**
   * Get admin statistics
   */
  async getAdminStats(): Promise<{
    pendingCount: number;
    approvedTodayCount: number;
    rejectedTodayCount: number;
    totalPublishedCount: number;
  }> {
    return apiGet('/events/admin/stats');
  }

  /**
   * Get all events for admin with filtering and pagination
   */
  async getAllEvents(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<{
    events: Event[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    return apiGet(`/events/admin/all?${searchParams.toString()}`);
  }

  /**
   * Cancel an event (soft delete → status CANCELLED). Attached groups keep
   * existing but the event goes offline. Admins may always cancel, also once
   * groups are attached.
   */
  async deleteEvent(eventId: number): Promise<void> {
    return apiDelete(`/events/${eventId}`);
  }
}

export const eventService = new EventService();
export const adminEventService = new AdminEventService();